import PermissionsLists from "@/config/PermissionsLists";
import { EndPoints, httpService, Operation } from "@/Core/Client";
import { useState } from "react";

import { Config } from "@/components/crud/crud-config";
import CrudFormElements from "@/components/crud/crud-form-elements";
import CrudPage from "@/components/crud/page";
import PencilIcon from "@/components/icons/pencil";
import TableActions from "@/components/table-actions";
import useQueryKeys from "@/config/query-keys";
import { ESocialMedia } from "@/Core/enums";
import { DATE_FORMAT_OPTIONS } from "@/Core/Helpers/DayjsHelpers";
import {
    EntityFormDto,
    entityFormSchema,
    EntityResponseDto,
} from "@/Dtos/entity-dto";
import AvatarLoaderCard from "@/ui/avatar-card-loader";
import DateCell from "@/ui/date-cell";
import { enumToOptions } from "@/utils/get-option-by-value";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { Checkbox } from "rizzui";
import { useModal } from "../shared/modal-views/use-modal";
import EyeIcon from "@/components/icons/eye";
import routes from "@/routers/routes";
import { useNavigate } from "react-router-dom";

type responseType = EntityResponseDto;
type formType = EntityFormDto;

const config: Config<responseType, formType> = {
    endPoint: EndPoints.entity.crud,
    queryKey: useQueryKeys.entity,
    columns: [],
    validationSchema: entityFormSchema,
    permissionsCreate: PermissionsLists.Entity.Create,
    formOptions: {
        customSize: "1200px",
        sendAsFormData: true,
    },
    titles: {
        pageTitle: "Entities",
    },
    filterProps: {
        useDrawer: true,
        sortingClassName: "w-auto",
        directionClassName: "w-auto",
    },
    fields: [
        {
            name: "name",
            label: "Name",
            type: "text",
            hasFilter: true,
            // className: "w-auto",
            filter: {
                operation: Operation.like,
                // showOperations: true,
            },
        },
        {
            name: "email",
            label: "Email",
            type: "email",
            hasFilter: true,
            filter: {
                operation: Operation.eq,
            },
        },
        {
            name: "phone",
            label: "Phone Number",
            type: "phone",
            hasFilter: true,
            filter: {
                operation: Operation.like,
            },
        },
        {
            name: "location",
            label: "Location",
            type: "text",
            hasFilter: true,
            filter: {
                operation: Operation.like,
            },
        },
        {
            name: "countryId",
            label: "Country",
            type: "referTo",
            hasFilter: true,
            // className: "w-auto",
            referEndPoint: EndPoints.country.referTo,
            filter: {
                operation: Operation.like,
                // showOperations: true,
            },
        },
        {
            name: "cityId",
            label: "City",
            type: "referTo",
            hasFilter: true,
            referEndPoint: EndPoints.city.referTo,
            filter: {
                operation: Operation.like,
                // showOperations: true,
            },
            dependsOn: {
                name: "countryId",
                filter: (e, watchedValue) =>
                    e?.["meta"]?.["countryId"].match(watchedValue),
            },
        },
        {
            label: "SocialMedias",
            name: "socialMedias",
            type: "array",
            arrayFields: [
                {
                    label: "Type",
                    name: "type",
                    type: "select",
                    options: enumToOptions(ESocialMedia),
                },
                {
                    label: "Link",
                    name: "link",
                    type: "url",
                },
            ],
        },
        {
            name: "description",
            label: "Description",
            type: "textarea",
            className: "col-span-full",
            props: {
                textareaClassName: "h-20",
            },
        },
        {
            name: "images",
            label: "Images",
            type: "multipleFiles",
            createFormOnly: true,
            props: {
                // multiple: true,
            },
        },
        {
            name: "createdAt",
            label: "Created At",
            type: "date",
            hasFilter: true,
            filterOnly: true,
            dateFormat: DATE_FORMAT_OPTIONS.L,
            filter: {
                operation: Operation.gte,
            },
        },
    ],
};

const columnHelper = createColumnHelper<responseType>();

const Columns = [
    columnHelper.display({
        id: "select",
        size: 50,
        header: ({ table }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select all Rows"
                checked={table.getIsAllPageRowsSelected()}
                onChange={() => table.toggleAllPageRowsSelected()}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select Row"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
    }),
    columnHelper.accessor("name", {
        id: "name",
        size: 200,
        header: "Name",
        enableSorting: true,
        cell: ({ row }) => (
            <AvatarLoaderCard
                name={row.original?.name ?? ""}
                description={row.original?.email ?? ""}
            />
        ),
    }),
    columnHelper.accessor("phone", {
        id: "phone",
        size: 150,
        header: "Phone Number",
        cell: ({ row }) => row.original.phone,
    }),
    columnHelper.accessor("city.name", {
        id: "city.name",
        size: 150,
        header: "Country/City",
        cell: ({ row: { original } }) => (
            <AvatarLoaderCard
                name={original.city?.country?.name ?? ""}
                description={original.city?.name ?? ""}
            />
        ),
    }),
    columnHelper.accessor("location", {
        id: "location",
        size: 140,
        header: "Address",
        cell: ({ row }) => (
            <span className="text-gray-700 capitalize">
                {row.original.location}
            </span>
        ),
    }),
    columnHelper.accessor("createdAt", {
        id: "createdAt",
        size: 160,
        header: "Created",
        cell: ({ row }) => (
            <DateCell
                date={new Date(Date.parse(row.original?.createdAt ?? ""))}
            />
        ),
    }),
    columnHelper.display({
        id: "actions",
        size: 80,
        cell: ({ row }) => {
            const { openModal } = useModal();
            const [isLoading, setLoading] = useState(false);
            const queryClient = useQueryClient();
            const navigate = useNavigate();

            return (
                <TableActions
                    row={row.original}
                    permissionDelete={PermissionsLists.Entity.Delete}
                    onDelete={() => {
                        setLoading(true);
                        httpService(config.endPoint).delete(row.original.id!);
                        queryClient.refetchQueries({
                            queryKey: config.queryKey,
                        });
                        setLoading(false);
                    }}
                    actions={[
                        {
                            title: "View",
                            isLoading: isLoading,
                            icon: <EyeIcon className="size-4" />,
                            onClick: () => navigate(routes.entity.details(row.original.id!)),
                        },
                        {
                            title: "Edit",
                            color: "gray",
                            isLoading: isLoading,
                            icon: <PencilIcon className="size-4" />,
                            permission: PermissionsLists.Entity.Edit,
                            onClick: () =>
                                openModal({
                                    view: (
                                        <CrudFormElements<
                                            responseType,
                                            formType
                                        >
                                            editData={{
                                                ...row.original,
                                                images: [],
                                            }}
                                            config={{
                                                ...config,
                                                fields: config?.fields?.filter(
                                                    (a) => !a.createFormOnly,
                                                ),
                                            }}
                                        />
                                    ),
                                    customSize: config.formOptions?.customSize,
                                }),
                        },
                    ]}
                />
            );
        },
    }),
];

config.columns = Columns;

export default function MainPage() {
    return CrudPage<responseType, formType>({ config: config });
}